/** Graphical attunement scene — overview grid of all skill trees with drill-down to per-tree V4 node graphs. */
import Phaser from 'phaser';
import type { GameScene } from './GameScene';
import type { V4NodeDefinition, V4ApotheosisDefinition } from '../utils/Types';
import type { LayoutTier } from '../ui/TreeLayoutEngine';
import { ConfigLoader } from '../systems/ConfigLoader';
import { TreeClusterRenderer } from '../ui/TreeClusterRenderer';
import { TreeDetailRenderer } from '../ui/TreeDetailRenderer';
import { SlotPanel } from '../ui/SlotPanel';
import { crispText, crispButton, crispGraphics, setupCrispCamera, CRISP_SCALE } from '../ui/CrispUtil';
import { Font, Hex, TextStyle, ButtonColor, Color } from '../ui/Theme';
import { GAME_WIDTH, GAME_HEIGHT } from '../utils/Constants';

type ViewMode = 'overview' | 'detail';

/**
 * Two-mode scene: overview renders compact tree clusters;
 * detail renders the full V4 node graph for one tree.
 * SlotPanel at the bottom handles equip/unequip.
 */
export class AttunementTreeScene extends Phaser.Scene {
  private gameSceneRef!: GameScene;
  private viewMode: ViewMode = 'overview';
  private selectedTree: string | null = null;

  // Renderers
  private clusterRenderer!: TreeClusterRenderer;
  private detailRenderer!: TreeDetailRenderer;
  private slotPanel!: SlotPanel;

  // Managed objects for current view
  private viewObjects: Phaser.GameObjects.GameObject[] = [];
  private pendingAttuneId: string | null = null;

  constructor() {
    super({ key: 'AttunementTreeScene' });
  }

  /** Initialises renderers, wires the slot panel and ESC handler, then opens the overview. */
  create(): void {
    this.gameSceneRef = this.scene.get('GameScene') as GameScene;
    setupCrispCamera(this);
    this.cameras.main.setBackgroundColor(Hex.sceneBg);

    // Title
    crispText(this, GAME_WIDTH / 2, 24, 'ATTUNEMENTS', TextStyle.debugTitle).setOrigin(0.5);

    // Renderers
    this.clusterRenderer = new TreeClusterRenderer(this);
    this.detailRenderer = new TreeDetailRenderer(this, {
      onNodeClick: (treeName, node, tier) => this.onNodeClick(treeName, node, tier),
      onNodeHover: () => {},
    });

    // Slot panel at bottom (compact)
    const slotPanelY = GAME_HEIGHT - 48;
    this.slotPanel = new SlotPanel(this, slotPanelY, this.gameSceneRef.player.attunement, {
      onSlotClick: (slotIndex, skillId) => this.onSlotAction(slotIndex, skillId),
      onSlotAssign: (slotIndex, skillId) => this.onSlotAssign(slotIndex, skillId),
      onRefreshNeeded: () => this.rebuildCurrentView(),
    });

    // ESC key — back navigation
    this.input.keyboard!.on('keydown-ESC', () => {
      if (this.pendingAttuneId) {
        this.pendingAttuneId = null;
        this.slotPanel.setPendingSkill(null);
        this.rebuildCurrentView();
      } else if (this.viewMode === 'detail') {
        this.showOverview();
      } else {
        this.scene.start('PauseScene');
      }
    });

    // Initial view
    this.showOverview();
  }

  /**
   * Switches to overview mode, laying out all tree clusters in a responsive grid
   * and drawing connector lines between them.
   */
  private showOverview(): void {
    this.clearView();
    this.viewMode = 'overview';
    this.selectedTree = null;

    const player = this.gameSceneRef.player;
    const treeNames = Object.keys(ConfigLoader.v4Trees);
    // TODO: filter to only discovered trees once discovery system exists

    // Grid layout — adapts to visible tree count
    const count = treeNames.length;
    const cols = count <= 4 ? 2 : Math.min(count, 3);
    const rows = Math.ceil(count / cols);

    const areaTop = 55;
    const areaBottom = GAME_HEIGHT - 65;
    const areaHeight = areaBottom - areaTop;
    const cellWidth = GAME_WIDTH / cols;
    const cellHeight = areaHeight / rows;

    // Compute cluster centers
    const centers: { cx: number; cy: number }[] = [];
    for (let i = 0; i < count; i++) {
      const col = i % cols;
      const row = Math.floor(i / cols);
      centers.push({
        cx: cellWidth * col + cellWidth / 2,
        cy: areaTop + cellHeight * row + cellHeight / 2,
      });
    }

    // Draw lines between all visible attunement clusters
    const S = CRISP_SCALE;
    const lineG = crispGraphics(this);
    this.viewObjects.push(lineG);
    lineG.lineStyle(0.5 * S, Color.treeBranch, 0.25);
    for (let i = 0; i < centers.length; i++) {
      for (let j = i + 1; j < centers.length; j++) {
        lineG.beginPath();
        lineG.moveTo(centers[i].cx * S, centers[i].cy * S);
        lineG.lineTo(centers[j].cx * S, centers[j].cy * S);
        lineG.strokePath();
      }
    }

    // Render clusters on top of lines
    for (let i = 0; i < count; i++) {
      const treeName = treeNames[i];
      const { cx, cy } = centers[i];

      const treeXp = player.skillProgression.getTreeXp(treeName);
      const currentTier = player.skillProgression.getCurrentTier(treeName);

      const isSelected = player.attunement.isTreeSelected(treeName);

      const zone = this.clusterRenderer.render(cx, cy, {
        treeName,
        currentTier,
        treeXp,
        isSelected,
      });

      zone.on('pointerdown', (_pointer: Phaser.Input.Pointer) => this.onClusterClick(treeName));

      // Deselect button for selected clusters
      if (isSelected) {
        const deselectBtn = crispButton(this, cx + 50, cy - 42, '×',
          Font.sizeButton, ButtonColor.muted, () => {
            player.attunement.deselectTree(treeName);
            this.showOverview();
          });
        this.viewObjects.push(deselectBtn);
      }
    }

    // Selection counter
    const selectedCount = player.attunement.getSelectedTrees().length;
    const counterText = crispText(this, GAME_WIDTH / 2, 45,
      `${selectedCount} / 3 SELECTED`, {
        fontFamily: Font.family, fontSize: Font.sizeLabel, color: Hex.textMuted,
      } as Phaser.Types.GameObjects.Text.TextStyle,
    ).setOrigin(0.5);
    this.viewObjects.push(counterText);

    // Rebuild slot panel
    this.slotPanel.rebuild();
  }

  /** Handle cluster click: selected trees enter detail, unselected get selected (if room). */
  private onClusterClick(treeName: string): void {
    const player = this.gameSceneRef.player;
    if (player.attunement.isTreeSelected(treeName)) {
      this.showDetail(treeName);
    } else {
      if (player.attunement.selectTree(treeName)) {
        this.showOverview();
      }
    }
  }

  /** Switches to detail mode, rendering the full V4 node graph for the given tree. */
  private showDetail(treeName: string): void {
    this.clearView();
    this.viewMode = 'detail';
    this.selectedTree = treeName;

    const treeDef = ConfigLoader.v4Trees[treeName];
    if (!treeDef) return;

    // Back button
    const backBtn = crispButton(this, 60, 50, '< BACK',
      Font.sizeButtonXs, ButtonColor.muted, () => this.showOverview());
    this.viewObjects.push(backBtn);

    // Tree icon + title
    const iconSize = 28;
    const iconGap = 8;
    const iconSpace = (iconSize + iconGap) / 2;

    const titleText = crispText(this, GAME_WIDTH / 2 + iconSpace, 50,
      treeName.toUpperCase(), {
        fontFamily: Font.family, fontSize: Font.sizeButton, color: Hex.textPrimary,
      } as Phaser.Types.GameObjects.Text.TextStyle,
    ).setOrigin(0.5);
    this.viewObjects.push(titleText);

    const titleLeft = titleText.x - titleText.displayWidth / 2;
    const treeIcon = this.add.image(titleLeft - iconGap - iconSize / 2, 50, 'attunement-icons', `tree-icon-${treeName}`)
      .setDisplaySize(iconSize, iconSize)
      .setOrigin(0.5);
    this.viewObjects.push(treeIcon);

    // Render full V4 tree detail
    const treeWidth = GAME_WIDTH - 100;
    const treeX = (GAME_WIDTH - treeWidth) / 2;
    this.detailRenderer.render(
      treeX, 70, treeWidth, GAME_HEIGHT - 140,
      treeName, treeDef,
      this.gameSceneRef.player.attunement,
      this.gameSceneRef.player.skillProgression,
    );

    // Rebuild slot panel
    this.slotPanel.rebuild();
  }

  /**
   * Handles a node click in the detail view. Resolves pending fork choices inline,
   * toggles attunement for unlocked active/evolution nodes, and begins slot-assignment
   * flow when a slot is available.
   */
  private onNodeClick(treeName: string, node: V4NodeDefinition | V4ApotheosisDefinition, tier: LayoutTier): void {
    const player = this.gameSceneRef.player;
    // Cancel pending attune if clicking a different node
    if (this.pendingAttuneId && this.pendingAttuneId !== node.id) {
      this.pendingAttuneId = null;
      this.slotPanel.setPendingSkill(null);
    }

    const isUnlocked = player.skillProgression.isUnlocked(node.id);

    // Pending fork — clicking a fork option chooses it directly
    if (!isUnlocked) {
      const forkInfo = ConfigLoader.getV4ForkForNode(node.id);
      if (forkInfo && forkInfo.treeName === treeName) {
        const pending = player.skillProgression.getPendingForks(treeName);
        const match = pending.find(f => f.forkId === forkInfo.fork.forkId);
        if (match) {
          player.skillProgression.chooseFork(treeName, match.forkId, node.id);
          this.rebuildCurrentView();
        }
      }
      return;
    }

    // Already unlocked — toggle attunement for active/evolution nodes
    if (node.nodeType === 'active' || node.nodeType === 'evolution') {
      if (player.attunement.isAttuned(node.id)) {
        player.attunement.unattune(node.id);
        if (player.skillActivation.isSkillActive(node.id)) {
          player.skillActivation.tryToggle(node.id);
        }
        this.rebuildCurrentView();
      } else if (player.attunement.canAttune(node.id)) {
        this.pendingAttuneId = node.id;
        this.slotPanel.setPendingSkill(node.id);
        this.rebuildCurrentView();
      }
    }
  }

  /** Unattunes the skill in the clicked slot and deactivates it if it was toggled on. */
  private onSlotAction(_slotIndex: number, skillId: string): void {
    const player = this.gameSceneRef.player;
    player.attunement.unattune(skillId);
    if (player.skillActivation.isSkillActive(skillId)) {
      player.skillActivation.tryToggle(skillId);
    }
    this.rebuildCurrentView();
  }

  /** Commits the pending skill to the chosen slot and clears the pending highlight. */
  private onSlotAssign(slotIndex: number, skillId: string): void {
    const player = this.gameSceneRef.player;
    player.attunement.attuneToSlot(skillId, slotIndex);
    this.pendingAttuneId = null;
    this.slotPanel.setPendingSkill(null);
    this.rebuildCurrentView();
  }

  /** Re-renders whichever view is currently active without losing the mode or selected tree. */
  private rebuildCurrentView(): void {
    if (this.viewMode === 'detail' && this.selectedTree) {
      this.showDetail(this.selectedTree);
    } else {
      this.showOverview();
    }
  }

  /** Destroys all view-scoped game objects and clears renderer state in preparation for a redraw. */
  private clearView(): void {
    this.clusterRenderer.clear();
    this.detailRenderer.clear();
    for (const obj of this.viewObjects) obj.destroy();
    this.viewObjects = [];
  }
}
